import React from 'react';
import styled from 'styled-components';

type ComposerMode = 'reply' | 'note';

interface Props {
	fromMode: ComposerMode;
	toMode: ComposerMode;
	onKeepDraft: () => void;
	onDiscard: () => void;
	onCancel: () => void;
}

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 50;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Backdrop = styled.div`
	position: absolute;
	inset: 0;
	background: rgb(0 0 0 / 0.4);
`;

const Dialog = styled.div`
	position: relative;
	z-index: 10;
	width: 100%;
	max-width: 24rem;
	border-radius: 8px;
	background: #ffffff;
	padding: 24px;
	box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
`;

const Heading = styled.h2`
	margin: 0 0 8px;
	font-size: 16px;
	line-height: 24px;
	font-weight: 500;
	color: #0f172a;
`;

const Body = styled.p`
	margin: 0 0 16px;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #475569;
`;

const Actions = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 8px;
`;

const BaseButton = styled.button`
	border: 0;
	border-radius: 4px;
	padding: 6px 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	cursor: pointer;
`;

const CancelButton = styled(BaseButton)`
	background: transparent;
	color: #475569;
	&:hover {
		background: #f1f5f9;
	}
`;

const DiscardButton = styled(BaseButton)`
	background: transparent;
	color: #dc2626;
	&:hover {
		background: #fef2f2;
	}
`;

const KeepButton = styled(BaseButton)`
	background: #2563eb;
	color: #ffffff;
	&:hover {
		background: #1d4ed8;
	}
`;

export function ConfirmModeSwitch({toMode, onKeepDraft, onDiscard, onCancel}: Props) {
	const targetLabel = toMode === 'note' ? 'Internal Note' : 'Reply';

	return (
		<Overlay>
			<Backdrop data-testid="modal-backdrop" onClick={onCancel} />
			<Dialog role="dialog" aria-labelledby="confirm-mode-switch-heading">
				<Heading id="confirm-mode-switch-heading">Switch to {targetLabel}?</Heading>
				<Body>You have unsaved content. What would you like to do?</Body>
				<Actions>
					<CancelButton type="button" onClick={onCancel}>
						Cancel
					</CancelButton>
					<DiscardButton type="button" onClick={onDiscard}>
						Switch anyway
					</DiscardButton>
					<KeepButton type="button" onClick={onKeepDraft}>
						Keep draft
					</KeepButton>
				</Actions>
			</Dialog>
		</Overlay>
	);
}
